import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { kycApi } from '@/api/kyc';
import { SHOWED_SECTIONS_COUNT } from '@/utils/static/kyc';
import { QuestionnaireView } from '@/modules/Questionnaire/View';
import { kycHelpers } from '@/utils/helpers/kyc';
import { processAnswersWithFiles } from '@/utils/helpers/kyc/answerProcessing';
import { useTranslation } from 'react-i18next';
import { usePopups, useLoadingInit } from '@/hooks';
import { downloadImgFromBlob, scrollToTop } from '@/utils/helpers';

export const SumsubQuestionnaire = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hideLoader, showLoader, showErrorToast } = usePopups();
  const { state } = useLocation();
  const [currentSection, setCurrentSection] = useState(0);
  const [preFilledQuestions, setPreFilledQuestions] = useState({});
  const [creators, setCreators] = useState({});
  const [defaultValues, setDefaultValues] = useState(null);
  const [disabledButton, setDisabledButton] = useState(false);

  const { data: questionnaire } = useQuery({
    queryKey: ['getSumSubQuestionnaire'],
    queryFn: () => kycApi.getSumSubQuestionnaire()
  });
  const questionnaireTypeGroup = questionnaire?.data?.[0]?.visibleTypeGroup;

  const { data: verificationPhotos } = useQuery({
    queryKey: ['getVerificationPhotos'],
    queryFn: () => kycApi.getVerificationPhotos(questionnaire.data[0].id),
    retry: false,
    enabled: !!questionnaireTypeGroup
  });
  const { mutateAsync: getVerificationPhoto } = useMutation({
    mutationFn: kycApi.getVerificationPhoto
  });

  const { data: sumSubStatusRes, refetch: refetchSumSub } = useQuery({
    queryKey: ['getSumSubStatus'],
    queryFn: kycApi.getSumSubStatus,
    refetchOnWindowFocus: true,
    refetchInterval: 30000
  });

  const {
    control,
    handleSubmit,
    trigger,
    watch,
    formState: { errors, submitCount, dirtyFields },
    reset
  } = useForm({ defaultValues: { ...defaultValues } });

  const handleVerificationPhoto = async (mediaId, imageTitle) => {
    try {
      const response = await getVerificationPhoto({
        questionSectionId: questionnaire.data[0].id,
        mediaId
      });

      if (response) {
        downloadImgFromBlob(response.data.data, `${imageTitle}.jpg`, 'image/jpeg', true);
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  useEffect(() => {
    if (questionnaire && questionnaire.data && sumSubStatusRes && sumSubStatusRes.data) {
      hideLoader();
    }
  }, [questionnaire, sumSubStatusRes]);

  useEffect(() => {
    try {
      if (questionnaire && questionnaire.data) {
        const { values, preFilledQuestions, creators } = kycHelpers.collectQuestionnaireAnswers({
          questionSections: questionnaire.data
        });

        if (Object.keys(preFilledQuestions).length > 0) {
          setPreFilledQuestions(preFilledQuestions);
        }
        if (Object.keys(creators).length > 0) {
          setCreators(creators);
        }

        setDefaultValues(values);
        reset(values);
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  }, [questionnaire]);

  useEffect(() => {
    reset();
  }, []);

  useLoadingInit();

  const stepsCount = useMemo(() => {
    if (questionnaire && questionnaire.data && questionnaire.data.questionSections)
      return Math.ceil(questionnaire.data.questionSections.length / SHOWED_SECTIONS_COUNT);
    return 0;
  }, [questionnaire]);

  const onNext = async (data) => {
    try {
      setDisabledButton(true);
      showLoader('Processing');

      const dirtyFieldsValues = kycHelpers.getDirtyValues(dirtyFields, data);

      const { array } = kycHelpers.collectQuestionnaireCurrentAnswers({
        filteredQuestionnaire: questionnaire.data,
        questionnaire: { questionSections: questionnaire.data },
        currentSection,
        data,
        dirtyFieldsValues
      });

      const { answersArray, files } = await processAnswersWithFiles(array, {
        validateFiles: true,
        t
      });

      try {
        const result = await kycApi.postSumSubAnswers({
          answers: answersArray,
          files: files,
          questionSectionId: questionnaire.data[0].id
        });

        if (result) {
          if (currentSection + 1 < stepsCount) setCurrentSection(currentSection + 1);
          else {
            kycApi
              .startSumSubCheck()
              .then(() => {
                navigate('/profile/questionnaires', {
                  state: {
                    verificationSubmitted: true
                  }
                });
                hideLoader();

                setDefaultValues(null);
                reset();
              })
              .catch((e) => {
                hideLoader();
                showErrorToast(e, true);
                console.error(e);
              });
          }
          scrollToTop({ behavior: 'smooth' });
        }
      } catch (e) {
        showErrorToast(e, true);
        console.error(e);
        hideLoader();
        setDisabledButton(false);
      }
    } catch (e) {
      showErrorToast(e, true);
      console.error(e);
      hideLoader();
      setDisabledButton(false);
    }
  };

  return (
    <>
      {questionnaire && questionnaire.data && sumSubStatusRes && sumSubStatusRes.data && (
        <QuestionnaireView
          questionnaire={{ questionSections: questionnaire.data }}
          filteredQuestionnaire={questionnaire.data}
          currentSection={currentSection}
          onNext={onNext}
          onBack={() =>
            state && state.prevPath ? navigate(state.prevPath, state.nextState) : navigate(-1)
          }
          control={control}
          handleSubmit={handleSubmit}
          trigger={trigger}
          watch={watch}
          errors={errors}
          submitCount={submitCount}
          preFilledQuestions={preFilledQuestions}
          creators={creators}
          disabledNext={disabledButton}
          isReadTooltip={sumSubStatusRes.data.investorVerificationQuestionSection.isReadTooltip}
          verificationPhotos={verificationPhotos}
          handleVerificationPhoto={handleVerificationPhoto}
          isVeriffQuestionnaire={true}
          sumSubStatusRes={sumSubStatusRes}
          refetchSumSubRes={refetchSumSub}
        />
      )}
    </>
  );
};
